import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { RotatingLines } from 'react-loader-spinner';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';
import { useParams } from 'react-router';

import CommonModal from '../../../../components/common-modal/CommonModal';
import AddProjectItem from './AddProjectItem';
import SearchInput from '../../../../components/common-inputs/SearchInput';
import useAccessProjectsOptions from '../../../customer-create/assign-projects/useAccessProjectsOptions';
import { addProjectToCompany } from '../../../../redux/customers/customersActions';

import scss from './AddProjectModal.module.scss';

function AddProjectModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const listRef = useRef(null);

  const { id: companyId } = useParams();

  const { projectSearch, accessProjects, handleSearchProjects, handleLoadMoreProjects } =
    useAccessProjectsOptions(true);
  const isAccessProjectLoading = useSelector((state) => state.customers.isAccessProjectsLoading);
  const totalAccessProjects = useSelector((state) => state.customers.accessProjectsTotal);

  const handleAddProject = (project) => {
    dispatch(addProjectToCompany({ projectId: project.id, companyId }));
    onClose();
  };

  const isRowLoaded = useCallback(({ index }) => !!accessProjects[index], [accessProjects]);

  const loadMoreRows = () => {
    if (!isAccessProjectLoading && accessProjects.length !== totalAccessProjects) {
      const newPage = page + 1;
      setPage(newPage);
    }
  };

  const scrollToIndex = (index) => {
    if (listRef.current) {
      listRef.current.scrollToRow(index);
    }
  };

  const projectsRowRenderer = ({ index, key, style }) => {
    const project = accessProjects[index];

    const listItemStyle = {
      ...style,
      marginBottom: 6,
    };

    return (
      <div key={key} style={listItemStyle}>
        <AddProjectItem onClick={handleAddProject} project={project} searchStr={projectSearch} />
      </div>
    );
  };

  useEffect(() => {
    if (page !== 1 && !isAccessProjectLoading) {
      handleLoadMoreProjects(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    setPage(1);
    scrollToIndex(0);
  }, [projectSearch]);

  useEffect(() => {
    if (!isOpen) {
      handleSearchProjects('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} wrapperClassName={scss.wrapper}>
      <div className={scss.contentWrapper}>
        <SearchInput value={projectSearch} size='sm' onChange={handleSearchProjects} />

        <div className={clsx('access_containers_container-test')}>
          <AutoSizer>
            {({ height, width }) => (
              <InfiniteLoader
                isRowLoaded={isRowLoaded}
                loadMoreRows={loadMoreRows}
                rowCount={accessProjects.length + 1}
                threshold={1}>
                {({ onRowsRendered, registerChild }) => (
                  <List
                    ref={(ref) => {
                      listRef.current = ref;
                      registerChild(ref);
                    }}
                    onRowsRendered={onRowsRendered}
                    rowRenderer={projectsRowRenderer}
                    rowCount={accessProjects.length}
                    rowHeight={46}
                    width={width}
                    height={height}
                  />
                )}
              </InfiniteLoader>
            )}
          </AutoSizer>

          {isAccessProjectLoading ? (
            <div className='loader-overlay'>
              <RotatingLines strokeColor='#fdbf47' strokeWidth='5' animationDuration='0.75' width={50} visible={true} />
            </div>
          ) : null}
        </div>
      </div>
    </CommonModal>
  );
}

export default AddProjectModal;
